<template>
    <div class='order-contract'>
        <div class="content">
            <div v-if="isPdfContract" class="pdf-wrapper">
                <div v-if="pdfLoading" class="pdf-status">正在加载合同...</div>
                <div v-else-if="pdfError" class="pdf-status pdf-status-error">
                    <p>{{ pdfError }}</p>
                    <button class="pdf-open-btn" @click="openContractInBrowser">在浏览器打开</button>
                </div>
                <div
                    v-show="!pdfLoading && !pdfError && !useEmbedFallback"
                    ref="pdfPages"
                    class="pdf-pages"
                ></div>
                <embed
                    v-if="useEmbedFallback"
                    :src="contractUrl"
                    type="application/pdf"
                    class="pdf-embed"
                />
            </div>
            <iframe
                v-else
                ref="contractIframe"
                :src="contractUrl"
                @error="handleIframeError"
                scrolling="true"
                frameborder="0"
                class="frame"
            ></iframe>
        </div>
        <button
            ref="backBtn"
            class='btn-back bg-color-red'
            :class="{ dragging: isDragging }"
            :style="{ top: btnTop + 'px' }"
            @click="handleBackClick"
            @touchstart="onDragStart"
            @touchmove.prevent="onDragMove"
            @touchend="onDragEnd"
            @mousedown="onDragStart"
        >返回订单</button>
    </div>
</template>
<script>
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.js";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL}pdf.worker.min.js`;

function parseContractUrl(raw) {
    let url = raw || "";
    if (Array.isArray(url)) {
        url = url[0] || "";
    }
    if (!url) {
        return "";
    }
    try {
        let decoded = decodeURIComponent(url);
        while (decoded !== url && decoded.indexOf("%") !== -1) {
            url = decoded;
            decoded = decodeURIComponent(url);
        }
        url = decoded;
    } catch (err) {
        // keep original url
    }
    return String(url).trim();
}

function checkPdfUrl(url) {
    const lower = (url || "").toLowerCase();
    const path = lower.split("#")[0].split("?")[0];
    return path.endsWith(".pdf") || path.includes("/contractdoc/");
}

export default {
    name: "OrderContract",
    components: {
    },
    props: {},
    data: function () {
        const contractUrl = parseContractUrl(this.$route.query.contractUrl);
        return {
            contractUrl,
            isPdfContract: checkPdfUrl(contractUrl),
            pdfLoading: false,
            pdfError: "",
            useEmbedFallback: false,
            btnTop: 0,
            dragStartY: 0,
            dragStartTop: 0,
            isDragging: false,
            hasMoved: false
        };
    },
    mounted: function () {
        window.addEventListener("message", this.handleMessage);
        window.addEventListener("mousemove", this.onDragMove);
        window.addEventListener("mouseup", this.onDragEnd);
        this.initBtnPosition();
        if (!this.contractUrl) {
            this.$dialog.error("合同链接不存在", () => {
                this.goback();
            });
            return;
        }
        if (this.isPdfContract) {
            this.$nextTick(() => {
                this.loadPdfPages();
            });
        }
    },
    beforeDestroy() {
        window.removeEventListener("message", this.handleMessage);
        window.removeEventListener("mousemove", this.onDragMove);
        window.removeEventListener("mouseup", this.onDragEnd);
    },
    methods: {
        async fetchPdfData() {
            const response = await fetch(this.contractUrl, {
                method: "GET",
                credentials: "same-origin"
            });
            if (!response.ok) {
                throw new Error("fetch failed");
            }
            return response.arrayBuffer();
        },

        async loadPdfPages() {
            const pagesEl = this.$refs.pdfPages;
            if (!pagesEl) {
                return;
            }

            this.pdfLoading = true;
            this.pdfError = "";
            this.useEmbedFallback = false;
            pagesEl.innerHTML = "";

            try {
                const pdfData = await this.fetchPdfData();
                const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
                const containerWidth = window.innerWidth || document.documentElement.clientWidth || 375;

                for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                    const page = await pdf.getPage(pageNum);
                    const baseViewport = page.getViewport({ scale: 1 });
                    const scale = containerWidth / baseViewport.width;
                    const viewport = page.getViewport({ scale: scale > 0 ? scale : 1 });

                    const canvas = document.createElement("canvas");
                    canvas.className = "pdf-page";
                    const outputScale = window.devicePixelRatio || 1;
                    canvas.width = Math.floor(viewport.width * outputScale);
                    canvas.height = Math.floor(viewport.height * outputScale);
                    canvas.style.width = viewport.width + "px";
                    canvas.style.height = viewport.height + "px";

                    const ctx = canvas.getContext("2d");
                    ctx.scale(outputScale, outputScale);

                    await page.render({
                        canvasContext: ctx,
                        viewport
                    }).promise;

                    pagesEl.appendChild(canvas);
                }

                if (!pdf.numPages) {
                    throw new Error("empty pdf");
                }
            } catch (err) {
                console.error(err);
                this.useEmbedFallback = true;
            } finally {
                this.pdfLoading = false;
            }
        },

        openContractInBrowser() {
            if (!this.contractUrl) {
                return this.$dialog.error("合同链接不存在");
            }
            window.open(this.contractUrl, "_blank");
        },

        handleIframeError() {
            this.$dialog.error("链接打开失败！");
        },

        // 处理接收到的消息
        handleMessage(event) {
            const receivedDataString = event.data;
            let receivedData;
            if(this.isString(receivedDataString)) {
                receivedData = JSON.parse(receivedDataString);
            } else {
                receivedData = receivedDataString;
            }
            if(receivedData.code !== 100000) {
                return;
            }
            this.$dialog.confirm({
                mes: "数据存在延迟，稍后再刷新订单列表",
                title: receivedData.msg,
                opts: [
                    {
                        txt: "返回",
                        color: false,
                        callback: () => {
                            this.$router.go(-1);
                         }
                    },
                    {
                        txt: "去首页",
                        color: true,
                        callback: () => {
                            this.$router.replace({ path: "/" });
                        }
                    }
                ]
            });
        },

        goback(){
            if (this.isPdfContract) {
                this.$router.go(-1);
                return;
            }
            this.$router.replace({ path: "/order/list/1" });
        },

        initBtnPosition() {
            this.$nextTick(() => {
                const btnHeight = this.getBtnHeight();
                this.btnTop = window.innerHeight - 160 - btnHeight;
            });
        },

        getBtnHeight() {
            return this.$refs.backBtn ? this.$refs.backBtn.offsetHeight : 36;
        },

        getClientY(e) {
            if (e.touches && e.touches.length) {
                return e.touches[0].clientY;
            }
            if (e.changedTouches && e.changedTouches.length) {
                return e.changedTouches[0].clientY;
            }
            return e.clientY;
        },

        onDragStart(e) {
            if (e.type === "mousedown" && e.button !== 0) {
                return;
            }
            this.isDragging = true;
            this.hasMoved = false;
            this.dragStartY = this.getClientY(e);
            this.dragStartTop = this.btnTop;
        },

        onDragMove(e) {
            if (!this.isDragging) {
                return;
            }
            const clientY = this.getClientY(e);
            const deltaY = clientY - this.dragStartY;
            if (Math.abs(deltaY) > 5) {
                this.hasMoved = true;
            }
            const btnHeight = this.getBtnHeight();
            const minTop = 10;
            const maxTop = window.innerHeight - btnHeight - 10;
            let newTop = this.dragStartTop + deltaY;
            this.btnTop = Math.max(minTop, Math.min(maxTop, newTop));
        },

        onDragEnd() {
            this.isDragging = false;
        },

        handleBackClick() {
            if (this.hasMoved) {
                this.hasMoved = false;
                return;
            }
            this.goback();
        },

        isString(value) {
            return typeof value === "string" || value instanceof String || Object.prototype.toString.call(value) === "[object String]";
        }
    }
};
</script>
<style>
.order-contract {
  background-color: #fff;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.order-contract .content {
    background-color: #f5f5f5;
    width: 100%;
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

.order-contract .content .frame {
  width: 100%;
  height: 100%;
  border: 0;
}

.order-contract .pdf-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.order-contract .pdf-pages {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 80px;
  box-sizing: border-box;
}

.order-contract .pdf-embed {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}

.order-contract .pdf-page {
  display: block;
  width: 100%;
  height: auto;
  margin: 0 auto 8px;
  background: #fff;
}

.order-contract .pdf-status {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
  box-sizing: border-box;
  font-size: 15px;
  color: #666;
  text-align: center;
}

.order-contract .pdf-status-error {
  flex-direction: column;
}

.order-contract .pdf-status-error p {
  margin: 0 0 16px;
}

.order-contract .pdf-open-btn {
  border: none;
  border-radius: 20px;
  padding: 10px 20px;
  background: #e93323;
  color: #fff;
  font-size: 14px;
}

.order-contract .btn-back {
  position: fixed;
  left: 85%;
  transform: translateX(-50%);
  z-index: 1000;
  color: white;
  padding: 8px 12px;
  min-height: 36px;
  border-radius: 20px;
  cursor: grab;
  font-size: 14px;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: box-shadow 0.3s ease;
  backdrop-filter: blur(10px);
  min-width: 80px;
  justify-content: center;
  touch-action: none;
  user-select: none;
  border: none;
  box-sizing: border-box;
}

.order-contract .btn-back.dragging {
  cursor: grabbing;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}

</style>
